import { useEffect, useState, useCallback } from 'react';
import { APP_CLIENT_LOOKUP_PATH } from '../constants/api';
import { LAST_APN_PROVIDER } from '../constants/storage';
import { AppURL } from '../constants/urls';

import providersService from '../services/providers';
import { AppConfig } from '../types/app';
import { IdProvider } from '../types/auth';
import { request, requestWithFallbackUrl } from '../utils/apiHelper';
import logger from '../utils/logger';
import {
  GandalfIDPEndpoints,
  getGandalfIDPEndpoints,
} from '../utils/endpointHelper';
import { shouldUseIdpConfigCustomDomain } from '../utils/featureEvaluator';

// https://tiny.amazon.com/5qqjir88
type GetIdPbyAppClientOutput = {
  identityproviders: { id: string }[];
};

export async function getIdentityProviders(
  config: AppConfig,
  clientId: string
): Promise<GetIdPbyAppClientOutput | undefined> {
  const getUrl = (endpoint: string) =>
    `${endpoint}${APP_CLIENT_LOOKUP_PATH}/${clientId}`;
  const endpoints: GandalfIDPEndpoints = getGandalfIDPEndpoints(config);
  const defaultUrl = getUrl(endpoints.default);
  const fetchOpts: RequestInit = {
    method: 'GET',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  };
  const options = { metricNs: 'GetClientConfig', treat404AsOk: true };
  const rolloutPercent = config.idpConfigCustomDomainRolloutPercent
    ? Number(config.idpConfigCustomDomainRolloutPercent)
    : undefined;
  const response = shouldUseIdpConfigCustomDomain(
    endpoints.customDomain,
    rolloutPercent
  )
    ? await requestWithFallbackUrl(
        getUrl(endpoints.customDomain!),
        defaultUrl, // Fallback URL
        fetchOpts,
        options
      )
    : await request(defaultUrl, fetchOpts, options);

  if (response.status === 404) return;
  return await response.json();
}

function useProviders(config: AppConfig) {
  // If the user was manually redirected to from Partner Central (APN)
  // to the alternative sign in methods page, we need to append all of
  // the URL parameters from their most recent APN sign in request to
  // ensure the sign in flow works as expected.
  if (window.location.pathname === AppURL.AlternativeSignInMethods) {
    const lastApnProviderString = localStorage.getItem(LAST_APN_PROVIDER);
    if (lastApnProviderString) {
      const lastApnProvider = JSON.parse(lastApnProviderString) as IdProvider;
      const searchParams = new URL(lastApnProvider.url).search;
      const urlSearchParams = new URLSearchParams(searchParams);
      const queryParameters = new URLSearchParams(window.location.search);
      // We expect Partner Central to include the partnerDomain parameter
      // on redirect to the alternative sign in methods page
      const partnerDomain = queryParameters.get('partnerDomain');
      window.history.replaceState(
        {},
        document.title,
        `${AppURL.AlternativeSignInMethods}?${urlSearchParams.toString()}${
          partnerDomain ? `&partnerDomain=${partnerDomain}` : ''
        }`
      );
    } else {
      throw new Error(
        'Invalid redirect to alternative sign in methods. User has no APN provider stored'
      );
    }
  }
  const clientId = providersService.getClientId();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [providers, setProviders] = useState<IdProvider[] | undefined>();

  const fetchProviders = useCallback(async () => {
    try {
      // Short circuit if the AppClient API is not enabled, get providers statically.
      if (config.enableAppClientApi !== 'true') {
        setProviders(providersService.getProvidersFromConfig(config, clientId));
        setIsLoading(false);
        return;
      }

      setError(undefined);
      setProviders(undefined);

      const result = await getIdentityProviders(config, clientId);

      if (!result?.identityproviders || !result?.identityproviders.length) {
        throw new Error('No providers have been enabled for the app client');
      }

      setProviders(
        result.identityproviders.map((idp) =>
          providersService.transformNameToIdProvider(
            idp.id,
            config.gandalfDomain
          )
        )
      );
      setIsLoading(false);
      return {
        isLoading,
        error,
        providers,
      };
    } catch (error: any) {
      logger.debug(error);
      setError(error);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, config]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  return {
    isLoading,
    error,
    providers,
  };
}

export default useProviders;
